import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { EditGeneralDetailsFlowSchema } from '../schemas/editGeneralDetailsFlowSchema'
import { Box, Field, Flex, Input, Message } from '@workwhile/ui'
import { useEditGeneralDetailsFlowContextValue } from '../context/EditGeneralDetailsFlowProvider'
import { useEverifySweepWarning } from '../hooks/useEverifySweepWarning'
import { CompanyTypeField } from 'components/features/Company/CompanyTypeField'
import { ModalSelect } from 'components/ModalSelect'
import { PastShiftCreationAllowedField } from './PastShiftCreationAllowedField'

type GeneralDetailsFormProps = {
  onSubmit: () => void
}

const DIRECTION_OPTIONS = [
  { label: 'before shifts start', value: 'before_shift_start' },
  { label: 'after shifts are created', value: 'after_shift_create' },
] as const

export const GeneralDetailsForm = ({ onSubmit }: GeneralDetailsFormProps) => {
  const {
    state: {
      mutationState: { errorMessage: mutationErrorMessage },
    },
  } = useEditGeneralDetailsFlowContextValue()

  const {
    formState: { errors },
    control,
    register,
  } = useFormContext<EditGeneralDetailsFlowSchema>()

  const sweepWarning = useEverifySweepWarning()

  // A fatal error blocks the save; the sweep warning is informational and doesn't.
  const fatalError =
    mutationErrorMessage || errors.currentValues?.message?.toString()
  let bottomMessage: { variant: 'error' | 'warning'; text: string } | null =
    null
  if (fatalError) {
    bottomMessage = { variant: 'error', text: fatalError }
  } else if (sweepWarning) {
    bottomMessage = { variant: 'warning', text: sweepWarning }
  }

  return (
    <Box
      as="form"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <Controller
        name="type"
        control={control}
        render={({ field }) => (
          <CompanyTypeField
            value={field.value}
            onChange={field.onChange}
            error={errors.type?.message?.toString()}
          />
        )}
      />

      <Box mt={3}>
        <Box mb={2}>Flex Tier is guaranteed to enter Reserve tier by:</Box>
        <Flex gap={3} alignItems="flex-start">
          <Field
            error={errors.terminalTierThresholdHours?.message?.toString()}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width="50%"
          >
            <Input
              {...register('terminalTierThresholdHours')}
              aria-label="Terminal tier threshold hours"
              placeholder="e.g. 24"
              rightContent={<Box pr={12}>hours</Box>}
            />
          </Field>

          <Controller
            name="terminalTierThresholdDirection"
            control={control}
            render={({ field }) => (
              <Field
                error={errors.terminalTierThresholdDirection?.message?.toString()}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                width="50%"
              >
                <ModalSelect
                  value={
                    DIRECTION_OPTIONS.find((o) => o.value === field.value) ??
                    null
                  }
                  onChange={(option) => field.onChange(option?.value)}
                  options={DIRECTION_OPTIONS}
                />
              </Field>
            )}
          />
        </Flex>
      </Box>

      <PastShiftCreationAllowedField />

      {bottomMessage ? (
        <Message variant={bottomMessage.variant} mt={3} borderRadius="small">
          {bottomMessage.text}
        </Message>
      ) : null}
    </Box>
  )
}
